import React from 'react';
import { array, bool, string, func } from 'prop-types';
import SparkMD5 from 'spark-md5';
import FileUpload from 'library/components/file-upload';
import DataQuery from 'library/util/query';
import { jsonToInputParams } from 'library/util/graph';
import { InputEvent as SyntheticEvent } from 'library/helpers/synthetic-events';
import { resolve } from 'library/helpers/path-resolver';

class FileS3Upload extends React.Component {
  static defaultProps = {
    allowMultiple: false,
    query: DataQuery,
    bucket: 'referral-resources',
    shouldCreateResource: true,
    value: [],
    required: false
  }

  state = {
    filename: null,
    links: []
  }

  static getDerivedStateFromProps (props, state) {
    // backwards compatiblity with other uses of file-s3-upload
    if (typeof props.value == 'string') {
      return {
        links: [
          {
            filename: props.value,
            signed_url: ''
          }
        ]
      };
    } else {
      return {
        links: resolve(state, 'links', []).length > 0 ? state.links : props.value
      };
    }
  }

  handleUpload = async (md5, file) => {
    const blob = await this.createBlob(md5, file);
    const presignedUrl = blob ? await this.getPresignedUrl(file, blob) : false;
    const uploadedStatus = presignedUrl ? await this.uploadTos3(presignedUrl, file) : false;

    if (this.props.shouldCreateResource
        && uploadedStatus == 200
        && this.props.bucket === 'referral-resources') {
      await this.createResource(blob, file);
    }
    this.props.onChange(new SyntheticEvent({
      name: this.props.name,
      value: [
        ...this.state.links.map((link) => link.blobKey),
        blob.key,
        blob.filename
      ]
    }));
    this.setState({ links: [...this.state.links, {filename: file.name, signed_url: '', blobKey: blob.key}] });
  }

  handleRemove = async (blobKey) => {
    const filteredLinks = this.state.links
      .filter((link) => link.blobKey !== blobKey);
    try {
      await this.props.onChange(new SyntheticEvent({
        name: this.props.name,
        value: filteredLinks.map(link => link.blobKey),
      }));
      this.setState({
        links: filteredLinks.length ? filteredLinks : [{filename: ''}]
      });
    } catch (err) {
      console.error(err);
    }
  }

  createMd5Checksum = (e) => {
    const file = e.target.files[0];
    const BlobSlice = File.prototype.slice || File.prototype.mozSlice || File.prototype.webkitSlice;
    const chunkSize = 2097152;
    const chunks = Math.ceil(file.size / chunkSize);
    const spark = new SparkMD5.ArrayBuffer();
    const fileReader = new FileReader();
    let currentChunk = 0;

    fileReader.onload = (e) => {
      spark.append(e.target.result);
      currentChunk++;

      if (currentChunk < chunks) {
        loadNext();
      } else {
        this.handleUpload(spark.end(), file);
      }
    };

    const loadNext = () => {
      const start = currentChunk * chunkSize;
      const end = ((start + chunkSize) >= file.size) ? file.size : start + chunkSize;

      fileReader.readAsArrayBuffer(BlobSlice.call(file, start, end));
    };

    loadNext(currentChunk, chunkSize, file, BlobSlice, fileReader);
  }

  createBlob = async (CShash, file) => {
    const params = {
      filename: file.name,
      checksum: CShash,
      bytesize: file.size,
      content_type: file.type
    };

    try {
      const { data } = await this.props.query(`
        mutation {
          create_blob(${jsonToInputParams(params)}){
            blob{
              id
              key
              signed_id
              filename
              content_type
              metadata
              byte_size
              checksum
            }
            success
            errors ${this.props.shouldCreateResource ? `` : ``}
          }
      }`);
      return data.create_blob.blob;

    } catch (e) {
      console.error(e);
    }
  }

  getPresignedUrl = async (file, blob) => {
    const response = await fetch('https://api.staging.pairin.com/v1/files', {
      method:'POST',
      body: JSON.stringify({
        bucket: this.props.bucket,
        filename: blob.key,
        type: file.type
      })
    }).then(res => res.json());

    return response.url;
  }

  uploadTos3 = async (url, file) => {
    const upload = await fetch(url,
      {
        headers: {
          'Content-Type': file.type,
        },
        method: 'PUT',
        body: file
      }
    );

    return upload.status;
  }

  createResource = async (blob, file) => {
    const params = {
      name: file.name,
      type: 'attached_file',
      blob_key: blob.signed_id,
    };

    try {
      const { data } = await this.props.query(`
        mutation {
          create_resource(${jsonToInputParams(params)}){
            success
            errors {
              path
              message
            }
          }
      }`);
      return data;
    } catch (e) {
      console.error(e);
    }
  }

  render = () => {
    const {
      links
    } = this.state;

    return (
      <FileUpload
        {...this.props}
        files={links}
        onChange={this.createMd5Checksum}
        onRemove={!this.props.shouldCreateResource ? this.handleRemove : null}
      />
    );
  }
}

FileS3Upload.propTypes = {
  allowMultiple: bool,
  bucket: string,
  onChange: func,
  name: string,
  value: array,
  query: func,
  className: string,
  shouldCreateResource: bool,
  required: bool
};

export default FileS3Upload;
